import { useEffect } from "react";
import { useSearchParams } from "next/navigation";

import { Action, Category } from "@/constants/events";
import { MenuItem } from "@/custom-types/MenuItem";
import { useEventTracker } from "@/hooks/useEventTracker";

import { MenuItemCardPlaceholder } from "@/components/botanic/MenuItemCard";
import MenuCard from "@/components/Dispensary/MenuCard";
import ShopNoResults from "@/components/ShopNoResults";

type Props = {
  hasActiveFilter: boolean;
  menuItems: MenuItem[];
  loading: boolean;
  className?: string;
  onResetFiltersClick: () => void;
};

const DispensaryMenuItems: React.FC<Props> = ({
  hasActiveFilter,
  menuItems,
  loading,
  onResetFiltersClick,
}) => {
  const searchParams = useSearchParams();
  const { publishEvent } = useEventTracker();

  const dealTitle = searchParams?.get("deal_title");
  const hasActiveDealFilters = dealTitle && dealTitle.length > 0;
  const headerText = hasActiveDealFilters
    ? "Leafly deals"
    : !hasActiveFilter
      ? "All products"
      : null;

  const menuItemsToShow = menuItems || [];

  useEffect(() => {
    if (!menuItemsToShow.length) {
      publishEvent({
        action: Action.impression,
        category: Category.dispensaryMenuFilter,
        label: "Darn! No results found",
      });
    }
  }, [menuItemsToShow]);

  if (!menuItemsToShow.length) {
    return hasActiveFilter ? (
      <ShopNoResults
        titleText="No matching results"
        primaryLinkText="reset filters"
        subtitleText="Try selecting fewer options or reset filters"
        action={() => {
          publishEvent({
            action: Action.click,
            category: Category.dispensaryMenuFilter,
            label: "reset all filters",
          });
          onResetFiltersClick();
        }}
        wrapperClassName="mb-xxl"
      />
    ) : null;
  } else {
    return (
      <>
        {headerText && (
          <h2 className="mb-sm heading--m font-extrabold">{headerText}</h2>
        )}
        {loading && (
          <div className="pb-md flex flex-col gap-md">
            {new Array(menuItemsToShow.length).fill(null).map((_, i) => (
              <MenuItemCardPlaceholder
                className="bg-white shadow-low"
                oneCardPerRow={true}
                key={`placeholder-${i}`}
              />
            ))}
          </div>
        )}

        {!loading && (
          <div className="pb-md flex flex-col gap-md">
            {(menuItemsToShow || []).map((item) => (
              <div data-testid="menu-card" key={item.id}>
                <MenuCard
                  dispensaryId={item.dispensary.id}
                  displayStaffPick={true}
                  menuItem={item}
                  oneCardPerRow={true}
                />
              </div>
            ))}
          </div>
        )}
      </>
    );
  }
};

export default DispensaryMenuItems;
